import React, { useEffect, useState } from "react";
import {
    Grid, Paper, Button, Select, MenuItem, InputLabel, FormControl,
    useMediaQuery, Typography, TableContainer, Table, TableHead,
    TableRow, TableCell, TableBody, TablePagination, Chip, Avatar,
    Box, Card, CardContent, Alert
} from "@mui/material";
import {
    ArrowDropDown, ArrowBack, Person, Email, Phone, Event as EventIcon, 
    AccessTime, LocationOn, People, CheckCircle, HelpOutline, Cancel,
    DateRange // Added for created at icon
} from "@mui/icons-material";
import Search from "../../../components/common/search/Search";
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { useDispatch } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";
import { toast } from "react-toastify";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import loaderGif from "../../../assets/images/loading.gif";

const ParticipantsList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { eventId } = useParams();
    const location = useLocation();
    
    // Get fallback data from location.state if available
    const { 
        eventData: fallbackEventData, 
        eventName: fallbackEventName,
        originalEvent: fallbackOriginalEvent
    } = location.state || {};

    const [participants, setParticipants] = useState([]);
    const [sortedParticipants, setSortedParticipants] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [responseCountsData, setResponseCountsData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [hasDataError, setHasDataError] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Response status mapping with enhanced styling
    const responseStatusMap = {
        1: { 
            label: "Yes, Will Attend", 
            color: "success", 
            bgColor: "#e8f5e8",
            textColor: "#2e7d32",
            icon: <CheckCircle fontSize="small" />
        },
        2: { 
            label: "No, I'm not attending", 
            color: "error", 
            bgColor: "#ffebee",
            textColor: "#d32f2f",
            icon: <Cancel fontSize="small" />
        },
        3: { 
            label: "Maybe Attending", 
            color: "warning", 
            bgColor: "#fff3cd",
            textColor: "#ed6c02",
            icon: <HelpOutline fontSize="small" />
        },
        0: { 
            label: "No Response", 
            color: "default", 
            bgColor: "#f5f5f5",
            textColor: "#757575",
            icon: <Cancel fontSize="small" />
        }
    };

    // Transform API participants data to match expected format
    const transformApiParticipants = (apiParticipants) => {
        if (!apiParticipants || !Array.isArray(apiParticipants)) return [];
        
        return apiParticipants.map((participant, index) => {
            // Find the response for the current event
            let currentEventResponse = null;
            if (participant.responses && Array.isArray(participant.responses)) {
                currentEventResponse = participant.responses.find(
                    response => response.event_id.toString() === eventId.toString()
                );
                console.log(`🔍 Participant ${participant.name} - Event ID: ${eventId}, Found Response:`, currentEventResponse);
            }

            const transformedParticipant = {
                id: participant.student_id || index + 1,
                user_id: participant.student_id,
                user_name: participant.name,
                user_email: participant.email,
                user_phone: participant.phone || "Not provided",
                profile_image: participant.profile_picture,
                response_status: currentEventResponse ? currentEventResponse.response : 0,
                responded_at: currentEventResponse ? (currentEventResponse.updated_at || currentEventResponse.created_at) : null,
                participant_created_at: participant.created_at, // Add participant created at
                event_id: eventId,
                response_id: currentEventResponse ? currentEventResponse.id : null
            };

            console.log(`✅ Transformed Participant ${participant.name}:`, transformedParticipant);
            return transformedParticipant;
        });
    };

    // API call to get event details and participants - following events page pattern
    const getEventDetails = () => {
        if (!eventId) {
            setHasDataError(true);
            toast.error("No event ID provided");
            return;
        }

        setIsLoading(true);
        setApiError(null);

        axiosInstance
            .post(`${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/events/view`, {
                id: parseInt(eventId)
            })
            .then((res) => {
                console.log("📊 Event Details API Response:", res?.data);
                
                if (res?.data?.status === 200) {
                    const responseData = res.data.data;
                    
                    // Set event data
                    setEventData(responseData.event);
                    
                    // Set response counts
                    setResponseCountsData(responseData.response_counts);
                    
                    // Transform and set participants
                    if (responseData.participants && responseData.participants.length > 0) {
                        console.log("📊 Raw Participants Data:", responseData.participants);
                        const transformedParticipants = transformApiParticipants(responseData.participants);
                        console.log("📊 Transformed Participants:", transformedParticipants);
                        setParticipants(transformedParticipants);
                        setSortedParticipants(transformedParticipants);
                        
                        toast.success(`Loaded ${transformedParticipants.length} participants successfully`);
                    } else {
                        // No participants yet
                        setParticipants([]);
                        setSortedParticipants([]);
                        toast.info("Event loaded successfully - No participants yet");
                    }
                    
                    setHasDataError(false);
                } else {
                    throw new Error(res?.data?.message || "Failed to load event details");
                }
            })
            .catch((err) => {
                console.error("📊 Event Details API Error:", err);
                
                let errorMessage = "Failed to load event details";
                if (err.response?.data?.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response?.status === 404) {
                    errorMessage = "Event not found";
                } else if (err.response?.status >= 500) {
                    errorMessage = "Server error loading event details";
                }
                
                setApiError(errorMessage);
                
                // Try to use fallback data if available
                if (fallbackEventData || fallbackOriginalEvent) {
                    setEventData(fallbackEventData || fallbackOriginalEvent);
                    setParticipants([]);
                    setSortedParticipants([]);
                    toast.warning("Using fallback event data - couldn't load latest details");
                } else {
                    setHasDataError(true);
                    toast.error(errorMessage);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSort = (filterValue) => {
        setSelectedFilter(filterValue);
        if (!filterValue) {
            setSortedParticipants(participants);
            return;
        }
        const filtered = participants.filter((participant) => 
            participant.response_status?.toString() === filterValue
        );
        setSortedParticipants(filtered);
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        const filteredData = participants.filter((participant) =>
            Object.values(participant).some((val) =>
                String(val).toLowerCase().includes(value.toLowerCase())
            )
        );
        setSortedParticipants(filteredData);
    };

    // Load event details on component mount
    useEffect(() => {
        if (eventId) {
            getEventDetails();
        } else {
            setHasDataError(true);
            toast.error("No event ID provided");
        }
    }, [eventId]);

    const handleBackToEvents = () => {
        navigate(-1); // Go back to previous page
    };

    // Function to navigate back to events list and retry
    const handleRetryFromEventsList = () => {
        // Navigate back to events list
        navigate('/events'); // Adjust this path based on your routing structure
    };

    const getStatusChip = (status) => {
        const statusInfo = responseStatusMap[status] || responseStatusMap[0];
        return (
            <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                size="small"
                style={{ 
                    backgroundColor: statusInfo.bgColor,
                    color: statusInfo.textColor,
                    fontWeight: '500'
                }}
            />
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "No response yet";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // New function to format created at date
    const formatCreatedDate = (dateString) => {
        if (!dateString) return "Not available";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPhoneNumber = (phone) => {
        if (!phone || phone === "Not provided") return "N/A";
        // Simple phone formatting
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    // Calculate statistics using API response counts or computed values
    const totalResponses = participants.length;
    const attendingCount = responseCountsData ? responseCountsData.attending : 
                          participants.filter(p => p.response_status === 1).length;
    const maybeCount = responseCountsData ? responseCountsData.maybe_attending : 
                      participants.filter(p => p.response_status === 3).length;
    const notAttendingCount = participants.filter(p => p.response_status === 2).length;
    const noResponseCount = participants.filter(p => p.response_status === 0).length;

    // Calculate attendance percentage
    const attendanceRate = totalResponses > 0 ? Math.round((attendingCount / totalResponses) * 100) : 0;

    return (
        <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', py: 3 }}>
            <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, sm: 3 } }}>
                {/* API Error Alert */}
                {apiError && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {apiError} - {eventData ? "Showing limited information." : "Unable to load event data."}
                    </Alert>
                )}

                {/* Data Error Alert */}
                {hasDataError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Unable to load event data. Please check the event ID and try again.
                    </Alert>
                )}

                {/* Header Section with Fixed Back Button */}
                <Box sx={{ mb: 4 }}>
                    {/* Back Button with improved styling */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            onClick={() => navigate(-1)}
                            variant="outlined"
                            sx={{ 
                                textTransform: 'none',
                                border: '0',
                                color: '#666',
                                textAlign: 'left',
                                display: 'inline-block',
                                width: 'auto',
                                '&:hover': {
                                    color: '#1976d2',
                                    border: '0',
                                    background: 'transparent'
                                }
                            }}
                        >Back to Previous Page
                        </Button>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 3 }}>
                        Event Responses & Participants
                    </Typography>
                    
                    {/* Event Info Card */}
                    {eventData && (
                        <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 3, 
                                    alignItems: 'flex-start',
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    {eventData.image && (
                                        <Box
                                            component="img"
                                            src={eventData.image}
                                            alt={eventData.name}
                                            sx={{
                                                width: { xs: '100%', sm: 128 },
                                                height: { xs: 200, sm: 96 },
                                                borderRadius: 2,
                                                objectFit: 'cover',
                                                boxShadow: 2
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
                                            {eventData.name || fallbackEventName}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                            {eventData.description}
                                        </Typography>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexWrap: 'wrap', 
                                            gap: 2,
                                            '& > div': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 3,
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }
                                        }}>
                                            {eventData.date && (
                                                <Box sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                                                    <EventIcon fontSize="small" />
                                                    <span>{eventData.date}</span>
                                                </Box>
                                            )}
                                            {eventData.time && (
                                                <Box sx={{ bgcolor: '#e8f5e8', color: '#388e3c' }}>
                                                    <AccessTime fontSize="small" />
                                                    <span>{eventData.time}</span>
                                                </Box>
                                            )}
                                            {eventData.location && (
                                                <Box sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}>
                                                    <LocationOn fontSize="small" />
                                                    <span>{eventData.location}</span>
                                                </Box>
                                            )}
                                            {/* Add Created At information */}
                                            {eventData.created_at && (
                                                <Box sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }}>
                                                    <DateRange fontSize="small" />
                                                    <span>Created: {formatCreatedDate(eventData.created_at)}</span>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Enhanced Statistics Cards */}
                    {!hasDataError && eventData && (
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                                        <People sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {totalResponses}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Total Participants
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                                        <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {attendingCount}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Confirmed Attending
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                                        <HelpOutline sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {maybeCount}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Maybe Attending
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                           
                        </Grid>
                    )}
                </Box>

                {/* Show error message when no data is available */}
                {hasDataError ? (
                    <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                            position: 'relative'
                        }}>
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Box sx={{ mb: 3 }}>
                                    <Cancel sx={{ 
                                        fontSize: 48, 
                                        color: '#f44336', 
                                        mb: 2,
                                        opacity: 0.8
                                    }} />
                                    <Typography variant="h6" sx={{ 
                                        color: '#d32f2f', 
                                        mb: 1,
                                        fontWeight: 600
                                    }}>
                                        Unable to Load Event Data
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: '#666',
                                        maxWidth: 400,
                                        mx: 'auto',
                                        lineHeight: 1.5
                                    }}>
                                        Failed to load event details. Please check the event ID and try again.
                                    </Typography>
                                </Box>

                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    justifyContent: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center'
                                }}>
                                    <Button 
                                        variant="contained"
                                        onClick={() => getEventDetails()}
                                        disabled={isLoading}
                                        sx={{ 
                                            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                            color: 'white',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            minWidth: { xs: '100%', sm: 200 },
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        {isLoading ? "Retrying..." : "Retry Loading"}
                                    </Button>
                                    
                                    <Button 
                                        variant="outlined"
                                        onClick={() => navigate(-1)}
                                        startIcon={<ArrowBack />}
                                        sx={{ 
                                            color: '#666',
                                            borderColor: '#ddd',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            minWidth: { xs: '100%', sm: 150 },
                                            '&:hover': {
                                                backgroundColor: 'white',
                                                borderColor: '#999',
                                                color: '#333'
                                            }
                                        }}
                                    >
                                        Go Back
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Filter and Search Section */}
                        <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={8}>
                                        <Search 
                                            value={searchQuery} 
                                            handleInputChange={(e) => handleSearch(e.target.value)}
                                            placeholder="Search by name, email, or phone..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Filter by Response Status</InputLabel>
                                            <Select
                                                value={selectedFilter}
                                                onChange={(e) => handleSort(e.target.value)}
                                                label="Filter by Response Status"
                                                IconComponent={(props) => (
                                                    <ArrowDropDown {...props} style={{ color: '#c942b1' }} />
                                                )}
                                            >
                                                <MenuItem value="">All Responses</MenuItem>
                                                <MenuItem value="1">✅ Yes, Will Attend</MenuItem>
                                                <MenuItem value="3">🤔 Maybe Attending</MenuItem>
                                                <MenuItem value="2">❌ No, I'm not attending</MenuItem>
                                                <MenuItem value="0">➖ No Response</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Show message when no participants but event data exists */}
                        {!isLoading && participants.length === 0 && !hasDataError && eventData && (
                            <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                                <CardContent sx={{ 
                                    p: 4, 
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                                    position: 'relative'
                                }}>
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Box sx={{ mb: 3 }}>
                                            <People sx={{ 
                                                fontSize: 48, 
                                                color: '#4caf50', 
                                                mb: 2,
                                                opacity: 0.7
                                            }} />
                                            <Typography variant="h6" sx={{ 
                                                color: '#2e7d32', 
                                                mb: 1,
                                                fontWeight: 600
                                            }}>
                                                No Participants Yet
                                            </Typography>
                                            <Typography variant="body2" sx={{ 
                                                color: '#666',
                                                maxWidth: 400,
                                                mx: 'auto',
                                                lineHeight: 1.5
                                            }}>
                                                This event is available but no one has signed up yet. Check back later or share the event to get more participants!
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        {/* Participants Table */}
                        <Card elevation={3} sx={{ borderRadius: 2 }}>
                            <CardContent sx={{ p: 0 }}>
                                {isLoading ? (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        justifyContent: 'center', 
                                        alignItems: 'center', 
                                        py: 8 
                                    }}>
                                        <Box 
                                            component="img"
                                            src={loaderGif} 
                                            alt="Loading..." 
                                            sx={{ width: 64, height: 64, mb: 2 }}
                                        />
                                        <Typography variant="body1" sx={{ color: '#666' }}>
                                            Loading event details and participants...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>S.No</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Participant</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Contact Information</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Response Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Response Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Joined Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sortedParticipants.length > 0 ? (
                                                    sortedParticipants
                                                        .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
                                                        .map((participant, idx) => (
                                                            <TableRow 
                                                                key={participant.id || idx} 
                                                                hover 
                                                                sx={{ 
                                                                    '&:hover': { backgroundColor: '#f8f9fa' },
                                                                    borderLeft: participant.response_status === 1 ? '4px solid #4caf50' : 
                                                                               participant.response_status === 3 ? '4px solid #ff9800' : 
                                                                               '4px solid #e0e0e0'
                                                                }}
                                                            >
                                                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                                                    {currentPage * rowsPerPage + idx + 1}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Avatar 
                                                                            src={participant.profile_image} 
                                                                            alt={participant.user_name}
                                                                            sx={{ width: 48, height: 48 }}
                                                                        >
                                                                            <Person />
                                                                        </Avatar>
                                                                        <Box>
                                                                            <Typography variant="body1" sx={{ fontWeight: '600', color: '#1a1a1a' }}>
                                                                                {participant.user_name || "Unknown User"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                                                User ID: {participant.user_id}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Email fontSize="small" sx={{ color: '#666' }} />
                                                                            <Typography variant="body2">
                                                                                {participant.user_email || "No email provided"}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Phone fontSize="small" sx={{ color: '#666' }} />
                                                                            <Typography variant="body2">
                                                                                {formatPhoneNumber(participant.user_phone)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {getStatusChip(participant.response_status)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: participant.responded_at ? '#1a1a1a' : '#999' }}>
                                                                        {formatDate(participant.responded_at)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: participant.participant_created_at ? '#1a1a1a' : '#999' }}>
                                                                        {formatCreatedDate(participant.participant_created_at)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                                                <People sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                                                                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                                                                    No participants yet
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: '#999' }}>
                                                                    {searchQuery ? "Try adjusting your search criteria" : "No one has responded to this event yet"}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        
                                        {/* Enhanced Pagination */}
                                        {sortedParticipants.length > 0 && (
                                            <TablePagination
                                                component="div"
                                                count={sortedParticipants.length}
                                                page={currentPage}
                                                onPageChange={(e, newPage) => setCurrentPage(newPage)}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={(e) => {
                                                    setRowsPerPage(parseInt(e.target.value, 10));
                                                    setCurrentPage(0);
                                                }}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                sx={{ 
                                                    borderTop: '1px solid #e0e0e0',
                                                    backgroundColor: '#fafafa'
                                                }}
                                            />
                                        )}
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ParticipantsList;